/**
 * ContactDataTab — silnik formatek (keystone unifikacja-formatek — mirror
 * `ContractorDataTab.tsx`/`CompanyDataTab.tsx`): pola core routowane przez
 * `renderField(fieldId, settings)` z propsów, dostarczany przez rodzica
 * (`ContractorContactDetail`) z `useKartotekaRenderField(contactEngineConfig, runtime)`.
 * Ten komponent pobiera TYLKO aktywny layout modułu Kontakty kontrahenta przez
 * `useSystemModuleActiveLayout('contractor_contacts')` (ten sam hook co rodzic —
 * honoruje layout przypięty w pozycji menu `?mi=`), iteruje po
 * sekcjach z `layout_config.tabs[0].sections` (zakładka „Dane" type='fields')
 * i dla każdego elementu typu 'field' deleguje do `renderField` (silnik sam
 * rozstrzyga core-vs-ext, metadane pola, gcrecord, custom widget vs natywna
 * delegacja) — DataTab nie zna już `SystemModuleField`/`fieldsQ`.
 *
 * Różnice od kontrahenta (Kontakty są prostsze, mirror `CompanyDataTab.tsx`):
 *  - brak TEXT[] outliers (tags/pkdCodes) i brak innego hardkodowanego pola poza
 *    silnikiem — WSZYSTKIE pola core idą przez `renderField`, więc ten komponent
 *    (jak Firmy) NIE potrzebuje `form`/`onChange`/`editable` — byłyby martwe.
 *  - picker kontrahenta (`contractorId`, create-only) zostaje na stronie
 *    (`ContractorContactDetail.tsx`), NAD zakładkami — nie jest core polem.
 *
 * Save flow w ContractorContactDetail (rodzic): JEDEN POST /contractors/:cId/contacts albo
 * PATCH /contractors/:cId/contacts/:id z `extValues` w tym samym body — bez osobnego PUT
 * ext-fields.
 */

import { Fragment, type ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { RecordLanguage } from '@saas/shared';

import { tryRenderLayoutElement } from '@/components/fields/LayoutElementRegistry';
import { type ContextRenderers, PLACEHOLDER_TYPES } from '@/components/form/RecursiveFormRenderer';
import { isFieldElementVisible, StyledFieldSlot } from '@/components/form/styled-field';
import { Section } from '@/components/kartoteki/CoreFieldLayout';
import { Skeleton } from '@/components/Skeleton';
import { useSystemModuleActiveLayout } from '@/hooks/useSystemModuleActiveLayout';
import { loadTabsFromConfig } from '@/pages/system-modules/layout-shape-adapter';

const MODULE_SLUG = 'contractor_contacts';

// ─── Type exports (używane w ContractorContactDetail) ───────────────────────

export interface ContactFormData {
  contractorId: string;
  title: string | null;
  firstName: string | null;
  lastName: string | null;
  position: string | null;
  department: string | null;
  email: string | null;
  phone: string | null;
  mobile: string | null;
  preferredChannel: string | null;
  language: RecordLanguage | null;
  birthday: string | null;
  linkedinUrl: string | null;
  isPrimary: boolean;
  isDecisionMaker: boolean;
  gdprConsentAt: string | null;
  notes: string | null;
  categoryId: string | null;
  /** Grupy rekordu — kody słownika `contractor_contact_groups`, WIELE na rekord (migracja 0119). */
  groups: string[];
}

export const EMPTY_CONTACT_FORM: ContactFormData = {
  contractorId: '',
  title: null,
  firstName: null,
  lastName: null,
  position: null,
  department: null,
  email: null,
  phone: null,
  mobile: null,
  preferredChannel: null,
  language: 'pl',
  birthday: null,
  linkedinUrl: null,
  isPrimary: false,
  isDecisionMaker: false,
  gdprConsentAt: null,
  notes: null,
  categoryId: null,
  groups: [],
};

/**
 * Słownik slug→stateKey dla pól core kontaktu (mirror `CONTRACTOR_CORE_FIELD_SLUGS`).
 * Slugi = `coreFields` modułu `contractor_contacts` w `apps/api/lib/seed-system-modules.ts`.
 * Służy do budowy `Record<slug, wartość>` na potrzeby warunków widoczności z kreatora — ORAZ
 * (silnik formatek) do budowy `contactEngineConfig.fields` z tego samego źródła prawdy (mirror
 * `CONTRACTOR_CORE_FIELD_SLUGS` w `contractorEngineConfig.tsx`). `contractorId` pominięty —
 * page-level FK (picker kontrahenta), nie jest pozycją katalogu `system_module_fields`.
 *
 * `category_id` DOPISANE przy migracji na silnik (wcześniej pominięte tu — stary komentarz
 * mówił „brak odpowiednika w katalogu pól", ale katalog GO MA od migracji 0127; renderował się
 * już przed silnikiem przez `CONTACT_CORE_RENDERERS.category_id`, tylko nie wchodził do tej mapy
 * — czyli nie był widoczny w `formData` warunków widoczności). Bez tego wpisu silnik nie miałby
 * dla niego configu w `contactEngineConfig.fields` i pole wpadłoby w bursztynowy fallback
 * „unsupported field" zamiast renderować `RecordCategoryPicker` jak dziś — patrz
 * `contactEngineConfig.tsx` nagłówek.
 */
export const CONTACT_CORE_FIELD_SLUGS: Record<string, keyof ContactFormData> = {
  title: 'title',
  first_name: 'firstName',
  last_name: 'lastName',
  position: 'position',
  department: 'department',
  email: 'email',
  phone: 'phone',
  mobile: 'mobile',
  preferred_channel: 'preferredChannel',
  language: 'language',
  birthday: 'birthday',
  linkedin_url: 'linkedinUrl',
  is_primary: 'isPrimary',
  is_decision_maker: 'isDecisionMaker',
  gdpr_consent_at: 'gdprConsentAt',
  notes: 'notes',
  groups: 'groups',
  category_id: 'categoryId',
};

/** Buduje `Record<slug, wartość>` z formularza kontaktu (do oceny warunków widoczności). */
export function buildContactCoreRecordValues(form: ContactFormData): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [slug, key] of Object.entries(CONTACT_CORE_FIELD_SLUGS)) {
    result[slug] = form[key];
  }
  return result;
}

// ─── Types dla fetch'owanego layoutu ─────────────────────────────────────────

interface LayoutElement {
  id: string;
  type: string;
  fieldId?: string;
  /** Ukrycie przez nakładkę per-tenant (systemowy layout) — pomijane na karcie. */
  hidden?: boolean;
  settings?: Record<string, unknown>;
}
interface LayoutSection {
  id: string;
  title: string | null;
  columns?: number;
  elements: LayoutElement[];
}

// ─── Props ─────────────────────────────────────────────────────────────────

export interface ContactDataTabProps {
  /**
   * Renderery elementów zależnych od kontekstu (record_links_view/attachments_view/…), te
   * same, które ContractorContactDetail przekazuje do RecursiveFormRenderer. Bez nich taki
   * element postawiony w zakładce „Dane" renderuje się jako martwy placeholder z generycznego
   * rejestru. Most do czasu przejścia kartoteki w całości na silnik (mirror
   * `ContractorDataTabProps`).
   *
   * UWAGA (różnica od `ContractorDataTabProps`): brak `form`/`onChange`/`editable` — Kontakty
   * (jak Firmy) nie mają hardkodowanego outliera poza silnikiem, więc te trzy propsy byłyby
   * martwe — pominięte celowo, nie przez przeoczenie.
   */
  contextRenderers?: ContextRenderers;
  /**
   * Renderuje pole core/ext po id — dostarczany przez rodzica z
   * `useKartotekaRenderField(contactEngineConfig, runtime)`. Silnik sam rozstrzyga
   * core-vs-ext, metadane pola (gcrecord/isCore/isRequired) i custom widget vs natywną
   * delegację — ten komponent tylko iteruje po layoucie.
   */
  renderField: (fieldId: string, settings?: Record<string, unknown>) => ReactNode;
  /**
   * Wartości formularza keyed po slugu pola (core + ext) — do oceny warunków widoczności
   * pól/bloków (`settings.conditionalField/Operator/Value`). Rodzic
   * (`ContractorContactDetail`) buduje ją z `buildContactCoreRecordValues(form)` + `extValues`.
   * Default `{}` — bez niej warunki traktujemy jak „zawsze widoczne".
   */
  formData?: Record<string, unknown>;
}

// ─── Główny komponent ──────────────────────────────────────────────────────

export function ContactDataTab({
  contextRenderers,
  renderField,
  formData = {},
}: ContactDataTabProps) {
  const { t } = useTranslation();

  // Aktywny layout — TEN SAM hook co rodzic: honoruje layout przypięty w pozycji menu
  // (`?mi=` → `layoutId`) i sam nakłada nakładkę per-tenant (`applyTabOverrides`).
  // Metadane pól żyją w silniku (useKartotekaRenderField, rodzic), ten komponent zna
  // tylko strukturę sekcji.
  const layoutQ = useSystemModuleActiveLayout(MODULE_SLUG);

  const { dataTabSections, dataTabExtras } = useMemo(() => {
    // F12.N post-E1.T7: backend zwraca NOWY rekurencyjny kształt
    // (`elements[0].tabs[].children`). Adapter `loadTabsFromConfig` rozpakowuje do starego
    // płaskiego `LayoutTab[]` z polami `sections` + `extras`. Nakładkę per-tenant (ukryte/zwężone
    // pola i formatki) nakłada już hook — tu NIE aplikujemy jej drugi raz.
    const cfg = layoutQ.layoutConfig ?? { elements: [] };
    const tabs = loadTabsFromConfig(cfg);
    const dataTab = tabs.find((tab) => tab.type === 'fields');
    return {
      dataTabSections: (dataTab?.sections ?? []) as LayoutSection[],
      // Elementy „luźne" zakładki Dane spoza sekcji (mirror ContractorDataTab/CompanyDataTab —
      // dziś Kontakty nie mają takiego bloku w seedzie, ale adapter jest wspólny, więc trzymamy
      // tę samą obsługę, żeby ewentualny loose block z buildera nie zniknął po cichu).
      dataTabExtras: (dataTab?.extras ?? []) as LayoutElement[],
    };
  }, [layoutQ.layoutConfig]);

  // Renderuje „luźny" element layoutu (blok-widok spoza sekcji) PEŁNĄ szerokością: przez
  // contextRenderers rodzica, a dla elementów bez kontekstu — przez generyczny rejestr
  // (heading/alert/…). Settings elementu lecą do renderera.
  function renderLooseElement(el: LayoutElement): ReactNode {
    const contextRenderer = PLACEHOLDER_TYPES.has(el.type)
      ? contextRenderers?.[el.type]
      : undefined;
    if (contextRenderer) {
      return (
        <Fragment key={el.id}>
          {contextRenderer({ element: { id: el.id, type: el.type, settings: el.settings } })}
        </Fragment>
      );
    }
    const layoutNode = tryRenderLayoutElement({
      element: {
        id: el.id,
        type: el.type,
        settings: (el.settings ?? {}) as Record<string, unknown>,
      },
      children: [],
      mode: 'form',
    });
    return layoutNode ? <Fragment key={el.id}>{layoutNode}</Fragment> : null;
  }

  if (layoutQ.isLoading) {
    return (
      <div className="space-y-3 p-4" data-testid="contact-data-tab-loading">
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
      </div>
    );
  }

  const showLayoutWarning = layoutQ.isError || (!layoutQ.isLoading && dataTabSections.length === 0);

  return (
    <div className="space-y-6" data-testid="contact-data-tab">
      {showLayoutWarning && (
        <div
          className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-700 dark:bg-amber-900/20 dark:text-amber-300"
          data-testid="contact-data-tab-no-layout"
        >
          {t('tenant.contractorContacts.detail.noLayoutWarning')}
        </div>
      )}

      {dataTabSections.map((section, sectionIdx) => (
        <Fragment key={section.id}>
          <Section title={section.title ?? t('tenant.contractorContacts.detail.untitledSection')}>
            {section.elements.map((el) => {
              // Ukrycie przez nakładkę per-tenant (systemowy layout) — pole/element z flagą
              // `hidden` nie renderuje się na karcie (builder pokazuje je wyszarzone).
              if (el.hidden === true) return null;
              // Warunek widoczności z kreatora (`settings.conditionalField/Operator/Value`
              // vs `formData`) — parytet z bogatym rendererem.
              if (!isFieldElementVisible(el, formData)) return null;
              // Non-field elementy (heading/divider/alert/...) renderowane przez
              // LayoutElementRegistry. Elementy zależne od kontekstu (record_links_view/…) muszą
              // iść przez contextRenderers rodzica — dokładnie jak w silniku
              // (RecursiveFormRenderer) i mirror ContractorDataTab.tsx/CompanyDataTab.tsx —
              // inaczej renderują się jako martwy placeholder z generycznego rejestru.
              if (el.type !== 'field') {
                const contextRenderer = PLACEHOLDER_TYPES.has(el.type)
                  ? contextRenderers?.[el.type]
                  : undefined;
                if (contextRenderer) {
                  // Bloki-widoki (powiązania, załączniki…) to pełnowymiarowe formatki, nie
                  // pojedyncze pole — w gridzie sekcji muszą zajmować cały wiersz.
                  return (
                    <div key={el.id} className="md:col-span-2">
                      {contextRenderer({
                        element: { id: el.id, type: el.type, settings: el.settings },
                      })}
                    </div>
                  );
                }
                const layoutNode = tryRenderLayoutElement({
                  element: {
                    id: el.id,
                    type: el.type,
                    settings: (el.settings ?? {}) as Record<string, unknown>,
                  },
                  children: [],
                  mode: 'form',
                });
                return layoutNode ? <Fragment key={el.id}>{layoutNode}</Fragment> : null;
              }

              if (!el.fieldId) return null;
              // Styl pola z kreatora (kolor etykiety/kontrolki) — scoped-CSS wokół
              // `renderField`, wspólny klocek z bogatym rendererem (styled-field).
              return (
                <StyledFieldSlot key={el.id} el={el} formData={formData}>
                  {renderField(el.fieldId, el.settings)}
                </StyledFieldSlot>
              );
            })}
          </Section>
          {/* Luźne elementy zakładki Dane spoza sekcji — patrz komentarz `dataTabExtras` wyżej. */}
          {sectionIdx === 0 && dataTabExtras.map(renderLooseElement)}
        </Fragment>
      ))}
      {/* Nietypowy layout bez żadnej sekcji — luźne elementy i tak pokaż. */}
      {dataTabSections.length === 0 && dataTabExtras.map(renderLooseElement)}
    </div>
  );
}
